/**
 * Promocao (spec 067, decisao 2.9): o destaque com a cara do evento no topo do checkout, o selo compacto do cartao de
 * plano e o preco "de / por". Sem regra: o servico de pagamentos diz se ha promocao, quanto ela tira, o preco com ela e
 * quando a ocorrencia de agora termina. A peca so mostra. Nao sabe a marca nem a moeda: os valores chegam formatados.
 * Os temas (MARCA, NOITE, FESTA, OFERTA) sao tokens do design system (`--promocao-<tema>-*`); tema desconhecido vira MARCA.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { contagemAte, contagemPorExtenso, type Contagem } from "../formatos";
import { IconeEtiqueta } from "../icones";

export const TEMAS_DA_PROMOCAO = ["MARCA", "NOITE", "FESTA", "OFERTA"] as const;
export type TemaDaPromocao = (typeof TEMAS_DA_PROMOCAO)[number];
/** O nome de cada tema pra quem escolhe (o painel). */
export const NOME_DO_TEMA: Record<TemaDaPromocao, string> = { MARCA: "Marca", NOITE: "Noite", FESTA: "Festa", OFERTA: "Oferta" };
const temaValido = (t: string | null | undefined): TemaDaPromocao => ((TEMAS_DA_PROMOCAO as readonly string[]).includes(t ?? "") ? (t as TemaDaPromocao) : "MARCA");

/**
 * A contagem ate `terminaEm`, atualizada a cada segundo. `null` quando nao ha fim a vista; `"acabou"` quando chegou a
 * zero (a peca some e quem usa busca o preco de novo).
 */
export function useContagem(terminaEm: string | null | undefined, aoTerminar?: () => void): Contagem | null | "acabou" {
  const calcular = (): Contagem | null | "acabou" => (terminaEm ? (contagemAte(terminaEm) ?? "acabou") : null);
  const [c, setC] = useState(calcular);
  const avisou = useRef(false);
  const ultimoAviso = useRef(aoTerminar);
  ultimoAviso.current = aoTerminar;
  useEffect(() => {
    avisou.current = false;
    setC(calcular());
    if (!terminaEm) return;
    const t = setInterval(() => setC(calcular()), 1000);
    return () => clearInterval(t);
  }, [terminaEm]);
  useEffect(() => {
    if (c !== "acabou" || avisou.current) return;
    avisou.current = true;
    ultimoAviso.current?.();
  }, [c]);
  return c;
}

const dois = (n: number) => String(n).padStart(2, "0");

/** O relogio da contagem: blocos de dias (so quando falta mais de um dia), horas, minutos e segundos. */
function Relogio({ c }: { c: Contagem }) {
  const blocos = [
    ...(c.dias ? [{ valor: String(c.dias), unidade: c.dias === 1 ? "dia" : "dias" }] : []),
    { valor: dois(c.horas), unidade: "h" },
    { valor: dois(c.minutos), unidade: "min" },
    { valor: dois(c.segundos), unidade: "s" },
  ];
  // O nome do relogio nao comeca por "Termina em": no painel, a previa fica ao lado do campo "Termina em".
  const nome = `Faltam ${contagemPorExtenso(c)}`;
  return (
    <div className="mtc-promocao__contagem">
      <span className="mtc-promocao__contagem-rotulo">Termina em</span>
      <div className="mtc-promocao__relogio" role="timer" aria-label={nome} title={nome}>
        {blocos.map((b) => (
          <span key={b.unidade} className="mtc-promocao__bloco" aria-hidden>
            <strong>{b.valor}</strong>
            <span>{b.unidade}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * A faixa da promocao no topo do checkout (e a previa no painel): o nome do evento, a chamada, o desconto e a contagem
 * regressiva quando ha fim a vista. `desconto` chega pronto ("30%", "R$ 100,00", "US$ 50,00"). Quando a contagem chega a
 * zero a faixa some e `aoTerminar` avisa (o checkout busca o preco de novo, sem a promocao).
 */
export function DestaqueDaPromocao({ nome, chamada, tema, desconto, terminaEm, aoTerminar }: { nome: string; chamada?: string | null; tema: string; desconto?: ReactNode; terminaEm?: string | null; aoTerminar?: () => void }) {
  const c = useContagem(terminaEm, aoTerminar);
  if (c === "acabou") return null;
  return (
    <section className="mtc-promocao" data-promocao={temaValido(tema)} role="region" aria-label="Promoção">
      <div className="mtc-promocao__textos">
        <p className="mtc-promocao__selo"><IconeEtiqueta tamanho={14} />Promoção</p>
        <p className="mtc-promocao__nome">{nome || "Nome da promoção"}</p>
        {chamada && <p className="mtc-promocao__chamada">{chamada}</p>}
      </div>
      {(desconto || c) && (
        <div className="mtc-promocao__lado">
          {desconto && (
            <span className="mtc-promocao__desconto" data-desconto>
              <strong>{desconto}</strong>
              <span>de desconto</span>
            </span>
          )}
          {c && <Relogio c={c} />}
        </div>
      )}
    </section>
  );
}

/** O selo compacto da promocao no cartao de plano: a etiqueta, o nome e, se vier, o desconto ("30%"). */
export function SeloDaPromocao({ nome, tema, desconto }: { nome: string; tema: string; desconto?: ReactNode }) {
  return (
    <span className="mtc-selo-promocao" data-promocao={temaValido(tema)} title={`Promoção ${nome}`}>
      <IconeEtiqueta tamanho={14} />
      <span className="mtc-selo-promocao__nome">{nome}</span>
      {desconto && <span className="mtc-selo-promocao__desconto">{desconto}</span>}
    </span>
  );
}

/**
 * O preco "de / por": o cheio riscado em cima, pequeno, e o promocional em `strong` (o lugar onde ele entra da o tamanho
 * e a cor, como no cartao "Sua compra" e no cartao de plano). `inicio` alinha a esquerda.
 */
export function PrecoDePor({ de, por, alinhar = "fim" }: { de: ReactNode; por: ReactNode; alinhar?: "inicio" | "fim" }) {
  return (
    <span className={`mtc-de-por${alinhar === "inicio" ? " mtc-de-por--inicio" : ""}`} data-de-por>
      <span className="mtc-de-por__de">de <del data-preco-de>{de}</del></span>
      <span className="mtc-de-por__por">por <strong data-preco-por>{por}</strong></span>
    </span>
  );
}
